import { type FC, Fragment, Suspense, lazy, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

/* Helpers */
import { locations } from '../../utils/tracking/shared/favoritesTracking.tsx';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { BuyerProductTileShimmer } from 'dibs-buyer-product-tile/exports/BuyerProductTileShimmer.ts';

import { useMergedSbAndSponsoredItems } from '../hooks/useMergedSbAndSponsoredItems.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useBuyerProductTileSize } from '../../shared/hooks/useBuyerProductTileSize.ts';

/* Components */
import { SbSharedProductAdWrapper } from '../SbSharedProductAdWrapper/SbSharedProductAdWrapper.tsx';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile.tsx';
import { QuickViewProvider } from 'dibs-search-product-tile/exports/QuickViewProvider.ts';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider.tsx';
import { useMeasurementUnit } from '../useMeasurementUnit.ts';
import { useSbSharedItemTracking } from '../hooks/sharedItemTracking/useSbSharedItemTracking.ts';
import { useSharedUrgencySignals } from '../hooks/sharedUrgencySignals/useSharedUrgencySignals.ts';
import { useItemShippingPrices } from '../hooks/useItemShippingPrices.ts';
import { usePartialItems } from '../hooks/usePartialItems.ts';
import { SbSharedPartialItemsPlaceholder } from '../SbSharedPartialItemsPlaceholder/SbSharedPartialItemsPlaceholder.tsx';

/* Constants */
import { VERTICAL_OFFSET_SB_RESP_IMG } from '../../constants/imageConstants.js';
import { STORYTELLING_MODULE_TYPE } from '../../constants/contentModuleConstants.js';

import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps.ts';
import { type SbRespSearchResultContainer_viewer$key } from './__generated__/SbRespSearchResultContainer_viewer.graphql.ts';
import { type SbRespSearchResultContainer_itemSearch$key } from './__generated__/SbRespSearchResultContainer_itemSearch.graphql.ts';
import { type SbRespSearchResultContainer_user$key } from './__generated__/SbRespSearchResultContainer_user.graphql.ts';
import { type SbRespSearchResultContainer_item$key } from './__generated__/SbRespSearchResultContainer_item.graphql.ts';

import { SbSharedContentModuleStorytellingWrapper } from '../SbSharedContentModuleStorytelling/SbSharedContentModuleStorytellingWrapper.tsx';

import styles from './sbRespSearchResultContainer.scss';

const MerchandizingTool = lazy(
    () =>
        import(
            /* webpackChunkName: "MerchandizingTool" */ 'dibs-merchandizing-tools/exports/MerchandizingTool.ts'
        )
);

const viewerFragment = graphql`
    fragment SbRespSearchResultContainer_viewer on Viewer {
        ...SharedItemTile_viewer
        ...useSbSharedItemTracking_viewer
    }
`;
const itemSearchFragment = graphql`
    fragment SbRespSearchResultContainer_itemSearch on ItemSearchQueryConnection {
        ...useMeasurementUnit_itemSearch
        ...useSbSharedItemTracking_itemSearch
        ...SbSharedProductAdWrapper_itemSearch
        ...SharedItemTile_itemSearch
        ...useMergedSbAndSponsoredItems_itemSearch
        ...SbSharedPartialItemsPlaceholder_itemSearch
        browseContentModule(contentModuleId: $contentModuleId) {
            moduleType
            ... on ContentModuleStorytelling {
                ...SbSharedContentModuleStorytellingWrapper_storytellingModule
            }
        }
    }
`;
const userFragment = graphql`
    fragment SbRespSearchResultContainer_user on User {
        serviceId
        hasMerchandizingPermission: hasPermission(name: "BUYER_FE_MERCH_TOOL")
        ...useMeasurementUnit_user
        ...SharedItemTile_user
    }
`;

const mergedItemsFragment = graphql`
    fragment SbRespSearchResultContainer_item on Item @relay(plural: true) {
        serviceId
        isSold
        ...usePartialItems_item
        ...FavoritesProvider_item
        ...useSharedUrgencySignals_item
        ...useItemShippingPrices_item
        ...useSbSharedItemTracking_item
        ...SharedItemTile_item
            @arguments(
                isTrade: $isTrade
                fetchVideo: $fetchTileVideo
                priceBookName: $priceBookName
                showSeller: $showSeller
            )
    }
`;

const ITEMS_PER_ROW = 3;
const LAZY_LOAD_ITEM_TILE_INDEX = ITEMS_PER_ROW * 2;

type Props = {
    viewer: SbRespSearchResultContainer_viewer$key;
    itemSearch: SbRespSearchResultContainer_itemSearch$key;
    user: SbRespSearchResultContainer_user$key;
    showMeasurements: boolean;
};

export const SbRespSearchResultContainer: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    user: userRef,
    showMeasurements,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);

    const browseContentModule = itemSearch?.browseContentModule || null;

    const { first: pageSize = 60, isClient } = useSbSelector(
        state => state.relayVariables.variables
    );
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);
    const [measurementUnit] = useMeasurementUnit({ itemSearch, user });
    const { isMobile, isItemFavoritingFeb2026Variant, isQuickFiltersRedesignVariant } =
        useServerVarsContext();
    const tileSize = useBuyerProductTileSize();

    const userId = user?.serviceId || '';
    const hasUserMerchandizingPermission = user?.hasMerchandizingPermission;

    const { mergedItems: mergedItemsRef, sponsoredItemsIndexMetadataMap } =
        useMergedSbAndSponsoredItems({ itemSearch });

    const mergedItems = useFragment<SbRespSearchResultContainer_item$key>(
        mergedItemsFragment,
        mergedItemsRef
    );

    const { fetchPartialItems, partialItemsIds, numOfInflatedItems, numOfPlaceholderItems } =
        usePartialItems(mergedItems);

    const inflatedItems = partialItemsIds.length
        ? mergedItems.slice(0, numOfInflatedItems)
        : mergedItems;

    const itemIds = [...new Set(mergedItems.map(item => item?.serviceId))].filter(filterFalsy);

    const { fetchItemUrgencySignals, getUrgencySignal } = useSharedUrgencySignals(mergedItems);
    const { fetchItemShippingPrices, getItemShippingPrices, getItemShippingTrackingData } =
        useItemShippingPrices(mergedItems);
    const { fireItemImpressionTracking, fireItemClickTracking } = useSbSharedItemTracking({
        viewer,
        itemSearch,
        items: mergedItems,
        getUrgencySignal,
        getItemShippingPrices,
        getItemShippingTrackingData,
    });

    const isStorytellingModule = browseContentModule?.moduleType === STORYTELLING_MODULE_TYPE;

    // Need to inflate all items asap for internal users to see merchandizing tool
    useEffect(() => {
        if (hasUserMerchandizingPermission) {
            fetchPartialItems();
        }
    }, [hasUserMerchandizingPermission, fetchPartialItems]);

    return (
        <div
            className={classnames(styles.gridContainer, {
                [styles.isQuickFiltersRedesignVariant]: isQuickFiltersRedesignVariant,
            })}
            data-tn="search-results-container"
        >
            {refetchInFlight ? (
                Array.from(Array(pageSize)).map((_, index) => (
                    <BuyerProductTileShimmer key={index} size={tileSize} />
                ))
            ) : (
                <FavoritesProvider
                    disable={!isClient}
                    items={mergedItems}
                    location={locations.SEARCH_BROWSE}
                    userId={userId}
                >
                    {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                        <QuickViewProvider>
                            <>
                                {inflatedItems.map((item, index) => {
                                    const isSponsored =
                                        sponsoredItemsIndexMetadataMap &&
                                        typeof sponsoredItemsIndexMetadataMap[index] !==
                                            'undefined';

                                    const itemKey = item?.serviceId
                                        ? `${item?.serviceId}${isSponsored ? '_SPONSORED' : ''}`
                                        : index;

                                    if (refetchInFlight) {
                                        return (
                                            <BuyerProductTileShimmer
                                                key={itemKey}
                                                size={tileSize}
                                            />
                                        );
                                    }
                                    return (
                                        <Fragment key={itemKey}>
                                            <SbSharedProductAdWrapper
                                                //finding original item index
                                                index={index}
                                                itemSearch={itemSearch}
                                                pageCol={isQuickFiltersRedesignVariant ? 4 : 3}
                                            />

                                            {isStorytellingModule && (
                                                <SbSharedContentModuleStorytellingWrapper
                                                    contentModule={browseContentModule}
                                                    index={index}
                                                    isMobile={false}
                                                    pageCol={isQuickFiltersRedesignVariant ? 4 : 3}
                                                />
                                            )}

                                            <SharedItemTile
                                                isSponsored={isSponsored}
                                                isVisuallySimilarEnabled
                                                item={item}
                                                user={user}
                                                favorites={favorites}
                                                index={index}
                                                showMeasurements={showMeasurements}
                                                lazyLoadStartIndex={LAZY_LOAD_ITEM_TILE_INDEX}
                                                imageLoadVerticalOffset={
                                                    VERTICAL_OFFSET_SB_RESP_IMG
                                                }
                                                beforeRender={() => {
                                                    fetchItemUrgencySignals(index);
                                                    fetchItemShippingPrices(index);
                                                }}
                                                onContentVisible={() => {
                                                    fireItemImpressionTracking({
                                                        itemId: item?.serviceId,
                                                        index,
                                                        isSponsored,
                                                    });
                                                }}
                                                onClick={() =>
                                                    fireItemClickTracking({
                                                        itemId: item?.serviceId,
                                                        index,
                                                        isSponsored,
                                                    })
                                                }
                                                measurementUnit={measurementUnit}
                                                viewer={viewer}
                                                itemSearch={itemSearch}
                                                fetchFolderOnMouseOver
                                                showFavoritesTooltip={
                                                    index === 0 &&
                                                    !(isMobile && isItemFavoritingFeb2026Variant)
                                                }
                                                hideRecentSoldPrice={!user && item?.isSold}
                                                urgencySignal={
                                                    getUrgencySignal(item.serviceId)?.message
                                                }
                                                shippingPrices={getItemShippingPrices(
                                                    item.serviceId
                                                )}
                                            />
                                        </Fragment>
                                    );
                                })}
                                <SbSharedPartialItemsPlaceholder
                                    itemSearch={itemSearch}
                                    numOfPlaceholderItems={numOfPlaceholderItems}
                                    fetchPartialItems={fetchPartialItems}
                                    numOfInflatedItems={numOfInflatedItems}
                                />
                                {hasUserMerchandizingPermission && (
                                    <Suspense fallback={null}>
                                        <MerchandizingTool
                                            itemIds={itemIds}
                                            userId={userId}
                                            refetchInFlight={
                                                refetchInFlight || !!numOfPlaceholderItems
                                            }
                                        />
                                    </Suspense>
                                )}
                            </>
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
            )}
        </div>
    );
};
